/**
 * SPORT GAMES ADMIN — Pilotage des jeux IPHB (MVP, Pronostic, Lots).
 * Authentification via PIN opérateur (réutilise X-Match-Operator-Pin).
 *
 * 3 onglets :
 *  - MVP : leaderboard live des votes
 *  - Pronos : liste des pronostics + déclaration du gagnant en fin de match
 *  - Lots : configuration des dotations sponsors (par session ou par défaut)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Target, Gift, ArrowLeft, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const PIN_KEY = "iphb_operator_pin";

function PinLogin({ onAuth }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.get("/match-live/operator/check", { headers: { "X-Match-Operator-Pin": pin } });
      if (r.data?.ok) {
        localStorage.setItem(PIN_KEY, pin);
        onAuth(pin);
        toast.success("Authentifié");
      } else {
        toast.error("PIN incorrect");
      }
    } catch {
      toast.error("Erreur de connexion");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: `radial-gradient(circle at top, ${PRIMARY}, #0D0220)` }}>
      <form onSubmit={submit} className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-md" data-testid="games-admin-pin-form">
        <div className="text-xs uppercase tracking-[0.3em] text-pink-300 mb-2">Gestion des Jeux</div>
        <h1 className="text-3xl font-black uppercase italic mb-1 text-white" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif" }}>Admin Jeux</h1>
        <p className="text-white/60 text-sm mb-6">PIN opérateur requis (même que la console match).</p>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="••••"
          className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/15 text-white text-xl tracking-widest text-center focus:outline-none focus:border-pink-400"
          data-testid="games-admin-pin-input"
        />
        <button
          type="submit"
          disabled={busy || !pin}
          className="w-full mt-4 py-3 rounded-lg font-bold uppercase tracking-wider text-white disabled:opacity-50 transition"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          data-testid="games-admin-pin-submit"
        >
          {busy ? "Vérification..." : "Entrer"}
        </button>
        <Link to="/sport/perfs-wall" className="block mt-4 text-center text-xs text-white/40 hover:text-white">← Retour Perfs Wall</Link>
      </form>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex-1 px-4 py-3 rounded-xl font-bold uppercase tracking-wider text-xs sm:text-sm transition flex items-center justify-center gap-2"
      style={{
        background: active ? `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` : "rgba(255,255,255,0.05)",
        color: active ? "#fff" : "rgba(255,255,255,0.6)",
        border: active ? "1px solid #ff3a8c" : "1px solid rgba(255,255,255,0.1)",
        boxShadow: active ? `0 8px 22px ${SECONDARY}55` : "none",
      }}
    >
      <Icon size={16}/> {label}
    </button>
  );
}

function SessionsPicker({ pin, value, onChange }) {
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    api.get("/match-live/sessions?limit=10", { headers: { "X-Match-Operator-Pin": pin } })
      .then((r) => setSessions(r.data?.rows || []))
      .catch(() => {});
  }, [pin]);
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm focus:outline-none focus:border-pink-400"
      data-testid="games-admin-session-picker"
    >
      <option value="">— Choisir une session —</option>
      {sessions.map((s) => (
        <option key={s.id} value={s.id}>
          {s.home_team} {s.home_score}-{s.away_score} {s.away_team} · {(s.created_at || "").slice(0, 10)} · {s.status}
        </option>
      ))}
    </select>
  );
}

function MvpTab({ sessionId }) {
  const [data, setData] = useState({ total: 0, rows: [] });
  const refresh = () => {
    if (!sessionId) return;
    api.get(`/match-live/votes/aggregate?session_id=${sessionId}`)
      .then((r) => setData(r.data || { total: 0, rows: [] }))
      .catch(() => {});
  };
  useEffect(() => { refresh(); const id = setInterval(refresh, 5000); return () => clearInterval(id); }, [sessionId]); // eslint-disable-line

  if (!sessionId) {
    return <div className="text-center text-white/50 text-sm py-12">Sélectionnez une session pour voir le classement MVP.</div>;
  }

  const max = data.rows?.[0]?.count || 1;

  return (
    <div className="space-y-4" data-testid="games-admin-mvp-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.25em] text-white/60">
          {data.total} vote{data.total > 1 ? "s" : ""} reçu{data.total > 1 ? "s" : ""}
        </div>
        <button onClick={refresh} className="text-xs text-pink-300 hover:text-pink-200 flex items-center gap-1.5"><RefreshCw size={12}/> Rafraîchir</button>
      </div>
      {data.rows.length === 0 ? (
        <div className="text-center text-white/50 text-sm py-12">Aucun vote pour le moment.</div>
      ) : (
        <div className="space-y-2">
          {data.rows.map((r, i) => (
            <div key={r.player_slug} className="rounded-xl p-3 border border-white/10 bg-white/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-black"
                    style={{ background: i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "rgba(255,255,255,0.1)", color: i < 3 ? "#000" : "#fff" }}>
                    {i + 1}
                  </div>
                  <div className="font-bold text-white text-sm">{r.player_name || r.player_slug}</div>
                </div>
                <div className="font-black text-pink-300">{r.count}</div>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full" style={{ width: `${(r.count / max) * 100}%`, background: `linear-gradient(90deg, ${SECONDARY}, #ff3a8c)` }}/>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PronosTab({ sessionId, pin }) {
  const [pronos, setPronos] = useState([]);
  const [winner, setWinner] = useState(null);
  const refresh = () => {
    if (!sessionId) return;
    api.get(`/match-live/pronos?session_id=${sessionId}`, { headers: { "X-Match-Operator-Pin": pin } })
      .then((r) => setPronos(r.data?.rows || [])).catch(() => {});
    api.get(`/match-live/prono/winner?session_id=${sessionId}`)
      .then((r) => setWinner(r.data?.winner || null)).catch(() => {});
  };
  useEffect(() => { refresh(); const id = setInterval(refresh, 5000); return () => clearInterval(id); }, [sessionId]); // eslint-disable-line

  if (!sessionId) {
    return <div className="text-center text-white/50 text-sm py-12">Sélectionnez une session pour voir les pronostics.</div>;
  }

  return (
    <div className="space-y-4" data-testid="games-admin-pronos-tab">
      {winner && (
        <div className="rounded-2xl p-5 border border-pink-400/40" style={{ background: `linear-gradient(135deg, ${SECONDARY}30, ${PRIMARY}40)` }}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-pink-300 font-bold mb-1">Gagnant du prono</div>
          <div className="font-black text-2xl text-white">{winner.voter_name}</div>
          <div className="text-sm text-white/80 mt-1">
            Pronostic : {winner.home_pred}-{winner.away_pred}
            {winner.submitted_at && <span className="text-white/50 ml-2">· {new Date(winner.submitted_at).toLocaleString("fr-FR")}</span>}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.25em] text-white/60">{pronos.length} pronostic{pronos.length > 1 ? "s" : ""}</div>
        <button onClick={refresh} className="text-xs text-pink-300 hover:text-pink-200 flex items-center gap-1.5"><RefreshCw size={12}/> Rafraîchir</button>
      </div>
      {pronos.length === 0 ? (
        <div className="text-center text-white/50 text-sm py-12">Aucun pronostic pour le moment.</div>
      ) : (
        <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
          {pronos.map((p, i) => (
            <div key={p.voter_key + i} className="rounded-lg p-3 border border-white/10 bg-white/5 flex items-center justify-between">
              <div>
                <div className="font-bold text-white text-sm">{p.voter_name || "Anonyme"}</div>
                <div className="text-[11px] text-white/45">{new Date(p.submitted_at).toLocaleString("fr-FR")}</div>
              </div>
              <div className="font-black text-lg text-pink-300">{p.home_pred} - {p.away_pred}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LotsTab({ sessionId, pin }) {
  const [cfg, setCfg] = useState({});
  const [busy, setBusy] = useState(false);
  const target = sessionId || "default";

  useEffect(() => {
    api.get(`/match-live/games-config${sessionId ? `?session_id=${sessionId}` : ""}`)
      .then((r) => setCfg(r.data?.config || {}))
      .catch(() => {});
  }, [sessionId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/match-live/games-config", { ...cfg, id: target },
        { headers: { "X-Match-Operator-Pin": pin } });
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error("Échec de l'enregistrement");
    } finally { setBusy(false); }
  };

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const Section = ({ title, prefix }) => (
    <div className="rounded-2xl p-5 border border-white/10 bg-white/5">
      <div className="text-xs uppercase tracking-[0.25em] text-pink-300 font-bold mb-4">{title}</div>
      <div className="space-y-3">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1">Lot offert</label>
          <input
            type="text"
            value={cfg[`${prefix}_prize`] || ""}
            onChange={(e) => update(`${prefix}_prize`, e.target.value)}
            placeholder="Ex : Maillot dédicacé + 2 places VIP"
            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm focus:outline-none focus:border-pink-400"
            data-testid={`games-admin-${prefix}-prize`}
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1">Sponsor (nom)</label>
          <input
            type="text"
            value={cfg[`${prefix}_sponsor_name`] || ""}
            onChange={(e) => update(`${prefix}_sponsor_name`, e.target.value)}
            placeholder="Ex : Crédit Mutuel"
            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm focus:outline-none focus:border-pink-400"
            data-testid={`games-admin-${prefix}-sponsor-name`}
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1">Logo sponsor (URL)</label>
          <input
            type="url"
            value={cfg[`${prefix}_sponsor_logo`] || ""}
            onChange={(e) => update(`${prefix}_sponsor_logo`, e.target.value)}
            placeholder="https://…/logo.png"
            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm focus:outline-none focus:border-pink-400"
            data-testid={`games-admin-${prefix}-sponsor-logo`}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="games-admin-lots-tab">
      <div className="text-xs uppercase tracking-[0.25em] text-white/60">
        Configuration {sessionId ? "pour la session sélectionnée" : "par défaut (toutes sessions)"}
      </div>
      <Section title="🏆 Vote MVP du match" prefix="mvp"/>
      <Section title="🎯 Jeu Pronostic du score" prefix="prono"/>
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="w-full py-3 rounded-xl font-bold uppercase tracking-wider text-white disabled:opacity-50 transition"
        style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, boxShadow: `0 10px 28px ${SECONDARY}55` }}
        data-testid="games-admin-save-config"
      >
        {busy ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}

export default function SportGamesAdmin() {
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_KEY) || "");
  const [tab, setTab] = useState("mvp");
  const [sessionId, setSessionId] = useState(null);

  // Vérifie le PIN au mount
  useEffect(() => {
    if (!pin) return;
    api.get("/match-live/operator/check", { headers: { "X-Match-Operator-Pin": pin } })
      .then((r) => { if (!r.data?.ok) { localStorage.removeItem(PIN_KEY); setPin(""); } })
      .catch(() => { localStorage.removeItem(PIN_KEY); setPin(""); });
  }, []); // eslint-disable-line

  if (!pin) return <PinLogin onAuth={setPin}/>;

  return (
    <div className="min-h-screen text-white" style={{ background: `radial-gradient(circle at top, ${PRIMARY}, #0D0220 60%, #050010)` }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/sport/perfs-wall" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 hover:text-white" data-testid="games-admin-back">
            <ArrowLeft size={14}/> Perfs Wall
          </Link>
          <button
            onClick={() => { localStorage.removeItem(PIN_KEY); setPin(""); }}
            className="text-xs text-white/40 hover:text-pink-300"
            data-testid="games-admin-logout"
          >Déconnexion</button>
        </div>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.3em] text-pink-300 mb-1">Pilotage IPHB</div>
          <h1 className="text-4xl font-black uppercase italic" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif" }}>
            Gestion des jeux
          </h1>
        </div>

        <div className="mb-5">
          <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1.5">Session match</label>
          <SessionsPicker pin={pin} value={sessionId} onChange={setSessionId}/>
        </div>

        <div className="flex gap-2 mb-6">
          <TabButton active={tab === "mvp"} onClick={() => setTab("mvp")} icon={Trophy} label="MVP" testId="games-admin-tab-mvp"/>
          <TabButton active={tab === "pronos"} onClick={() => setTab("pronos")} icon={Target} label="Pronos" testId="games-admin-tab-pronos"/>
          <TabButton active={tab === "lots"} onClick={() => setTab("lots")} icon={Gift} label="Lots" testId="games-admin-tab-lots"/>
        </div>

        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {tab === "mvp" && <MvpTab sessionId={sessionId}/>}
          {tab === "pronos" && <PronosTab sessionId={sessionId} pin={pin}/>}
          {tab === "lots" && <LotsTab sessionId={sessionId} pin={pin}/>}
        </motion.div>
      </div>
    </div>
  );
}
